import { useCallback, useEffect, useRef, useState } from "react";

export interface Loaded<T> {
  data?: T;
  error?: string;
  loading: boolean;
  reload(): Promise<void>;
  setData(d: T | ((prev: T | undefined) => T)): void;
}

/** Load data for a screen; re-runs when deps change. Ignores results from stale calls. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const call = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);
  const reload = useCallback(async () => {
    const n = ++call.current;
    setLoading(true);
    try {
      const d = await run();
      if (n === call.current) { setData(d); setError(undefined); }
    } catch (e) {
      if (n === call.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (n === call.current) setLoading(false);
    }
  }, [run]);
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData: setData as Loaded<T>["setData"] };
}

/** Wrap an async action with busy/error state for buttons. */
export function useAction<A extends unknown[], R>(fn: (...a: A) => Promise<R>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const run = useCallback(async (...a: A): Promise<R | undefined> => {
    setBusy(true);
    setError(undefined);
    try { return await fn(...a); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); return undefined; }
    finally { setBusy(false); }
  }, [fn]);
  return { run, busy, error, setError };
}
